import {
  Box,
  Dialog,
  DialogContent,
  Skeleton,
  Typography
} from '@customink/pigment-react';
import {styled} from '@customink/pigment-react/lib/themeCustomink';
import React from 'react';
import useGetQuickViewData from 'features/InternalTools/hooks/useGetQuickViewData';
import getQuickViewRenderScale from 'utils/mms';
import {handleModalClose} from 'utils/modal';
import QuickViewColorSwatches from './QuickViewColorSwatches';
import QuickViewPopup from './QuickViewPopup';
import QuickViewQuotes from './QuickViewQuotes';

const QuickViewTable = styled('table')({
  '& tbody': {
    '& tr > td:first-of-type': {
      whiteSpace: 'nowrap',
      width: '20%'
    },
    '& tr:nth-of-type(2n)': {
      backgroundColor: 'gainsboro'
    }
  },
  height: '100%',

  width: '100%'
});

const QuickViewCell = styled('td')({
  padding: '0.375rem 0.75rem',

  verticalAlign: 'top'
});

const SkeletonRow = ({multiline = false}) => (
  <Box sx={{display: 'flex', mb: 2}}>
    <Box sx={{pr: 2, width: '20%'}}>
      <Skeleton variant="text" width="90%" />
      {multiline && <Skeleton variant="text" width="50%" sx={{mt: 0.5}} />}
    </Box>
    <Box sx={{width: '75%'}}>
      <Skeleton variant="text" width="100%" />
      {multiline && (
        <>
          <Skeleton variant="text" width="90%" sx={{mt: 0.5}} />
          <Skeleton variant="text" width="80%" sx={{mt: 0.5}} />
        </>
      )}
    </Box>
  </Box>
);

const QuickViewSkeleton = () => {
  return (
    <Box sx={{padding: '0.375rem 0.75rem 0.5rem'}}>
      <Skeleton variant="text" width="40%" height={30} sx={{mb: 3}} />
      {/* Color swatches */}
      <Box sx={{display: 'flex', mb: 2}}>
        <Box sx={{pr: 2, width: '20%'}}>
          <Skeleton variant="text" width="90%" />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.5rem',
            pb: '0.5rem',
            width: '75%'
          }}>
          {Array.from({length: 6}).map((_, i) => (
            <Skeleton
              // eslint-disable-next-line react/no-array-index-key
              key={`swatch-${i}`}
              variant="rectangular"
              width="1.5rem"
              height="1.5rem"
              sx={{borderRadius: '0.25rem'}}
            />
          ))}
        </Box>
      </Box>
      <SkeletonRow multiline /> {/* Sizes */}
      <SkeletonRow /> {/* Turn Times */}
      <SkeletonRow /> {/* Individual-ship */}
      <SkeletonRow /> {/* International-ship */}
      <SkeletonRow /> {/* Ink Limit */}
      <SkeletonRow /> {/* Scale Factor */}
      <SkeletonRow multiline /> {/* Price */}
      <SkeletonRow /> {/* Decoration Type */}
      <SkeletonRow /> {/* Style # */}
      <SkeletonRow /> {/* Manufacturer */}
      <SkeletonRow multiline /> {/* Material */}
      <SkeletonRow multiline /> {/* Features */}
      <SkeletonRow /> {/* Sizing */}
      <SkeletonRow /> {/* Additional Print Areas */}
      <SkeletonRow /> {/* Decoration Days */}
      <SkeletonRow /> {/* Specialty */}
      <SkeletonRow /> {/* Other Notes */}
    </Box>
  );
};

const QuickView = ({onClose, productPath, styleId}) => {
  const handleOnClose = () => {
    onClose('');
    handleModalClose();
  };
  const {isError, isLoading, quoteData, style} = useGetQuickViewData(styleId);

  return (
    <Dialog open onClose={handleOnClose} width="lg">
      <DialogContent dividers sx={{paddingBottom: '0.75rem'}}>
        {isLoading && <QuickViewSkeleton />}

        {isError && !isLoading && (
          <Box sx={{padding: '1rem', textAlign: 'center'}}>
            <Typography variant="h6" color="error" gutterBottom>
              Error Loading Style
            </Typography>
            <Typography color="textSecondary">
              Could not load style information. Please try again later.
            </Typography>
          </Box>
        )}

        {!isLoading && !isError && style && (
          <>
            <Typography
              variant="h5"
              sx={{padding: '0.375rem 0.75rem 0.5rem'}}
              fontWeight={700}
              fontSize={20}
              lineHeight={1.25}>
              {style.name}
            </Typography>
            <QuickViewTable>
              <tbody>
                <QuickViewColorSwatches colors={style.colors} />
                <tr>
                  <QuickViewCell>
                    Sizes
                    <br />
                    <QuickViewPopup styleId={styleId} type="sizinginfo">
                      View Sizing Guide
                    </QuickViewPopup>
                  </QuickViewCell>
                  <QuickViewCell data-testid="quickview-sizes">
                    {style.sizes?.join(', ')}
                  </QuickViewCell>
                </tr>
                <tr>
                  <QuickViewCell>Turn Times</QuickViewCell>
                  <QuickViewCell data-testid="quickview-turntimes">
                    Rush {style.turntime.rush} Days/ Standard{' '}
                    {style.turntime.standard} Days
                  </QuickViewCell>
                </tr>
                <tr>
                  <QuickViewCell>Individual-ship Eligible</QuickViewCell>
                  <QuickViewCell data-testid="quickview-individual-ship">
                    {style.individual_ship_eligible ? 'Yes' : 'No'}
                  </QuickViewCell>
                </tr>
                <tr>
                  <QuickViewCell>International-ship Eligible</QuickViewCell>
                  <QuickViewCell data-testid="quickview-international-ship">
                    {style.international_ship_eligible ? 'Yes' : 'No'}
                  </QuickViewCell>
                </tr>
                <tr>
                  <QuickViewCell>Ink Limit</QuickViewCell>
                  <QuickViewCell data-testid="quickview-color-limit">
                    {style.color_limit}
                  </QuickViewCell>
                </tr>
                <tr>
                  <QuickViewCell>Scale Factor</QuickViewCell>
                  <QuickViewCell data-testid="quickview-scale-factor">
                    {getQuickViewRenderScale(style)}
                  </QuickViewCell>
                </tr>
                <tr>
                  <QuickViewCell>
                    Price (1 color on 1 side)
                    <br />
                    <QuickViewPopup styleId={styleId} type="quickquote">
                      Get a Quick Quote
                    </QuickViewPopup>
                  </QuickViewCell>
                  <QuickViewCell data-testid="quickview-quick-quote">
                    <QuickViewQuotes quoteData={quoteData} />
                  </QuickViewCell>
                </tr>
                <tr>
                  <QuickViewCell>Decoration Type</QuickViewCell>
                  <QuickViewCell data-testid="quickview-decoration-method">
                    {style.decoration_method}
                  </QuickViewCell>
                </tr>
                <tr>
                  <QuickViewCell>Style #</QuickViewCell>
                  <QuickViewCell data-testid="quickview-mill-no">
                    {style.mill_no}
                  </QuickViewCell>
                </tr>
                <tr>
                  <QuickViewCell>Manufacturer</QuickViewCell>
                  <QuickViewCell data-testid="quickview-manufacturer">
                    {style.manufacturer}
                  </QuickViewCell>
                </tr>
                <tr>
                  <QuickViewCell>Material</QuickViewCell>
                  <QuickViewCell
                    data-testid="quickview-material"
                    dangerouslySetInnerHTML={{
                      __html: style.material
                        ?.replace('*', '')
                        .replace(/\*/g, '<br/>')
                    }}
                  />
                </tr>
                <tr>
                  <QuickViewCell>Features</QuickViewCell>
                  <QuickViewCell
                    data-testid="quickview-features"
                    dangerouslySetInnerHTML={{
                      __html: style.features
                        ?.replace('*', '')
                        .replace(/\*/g, '<br/>')
                    }}
                  />
                </tr>
                <tr>
                  <QuickViewCell>
                    Sizing
                    <br />
                    <QuickViewPopup styleId={styleId} type="availability">
                      Availability
                    </QuickViewPopup>
                  </QuickViewCell>
                  <QuickViewCell data-testid="quickview-sizing">
                    {style.sizing}
                  </QuickViewCell>
                </tr>
                <tr>
                  <QuickViewCell>
                    Additional Print Areas
                    <br />
                    <QuickViewPopup
                      productPath={productPath}
                      styleId={styleId}
                      type="constraints">
                      Constraints
                    </QuickViewPopup>
                  </QuickViewCell>
                  <QuickViewCell data-testid="quickview-print-areas">
                    {style.additional_print_areas}
                  </QuickViewCell>
                </tr>
                <tr>
                  <QuickViewCell>Decoration Days</QuickViewCell>
                  <QuickViewCell data-testid="quickview-decoration-days">
                    {style.standard_decoration_days ?? ''}
                  </QuickViewCell>
                </tr>
                <tr>
                  <QuickViewCell>Specialty</QuickViewCell>
                  <QuickViewCell
                    data-testid="quickview-specialty"
                    dangerouslySetInnerHTML={{
                      __html: style.specialty
                    }}
                  />
                </tr>
                <tr>
                  <QuickViewCell>Other Notes</QuickViewCell>
                  <QuickViewCell
                    data-testid="quickview-notes"
                    dangerouslySetInnerHTML={{
                      __html: style.supplier_notes ?? ''
                    }}
                  />
                </tr>
              </tbody>
            </QuickViewTable>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QuickView;
